import { useTextToSpeech } from '@hakimo-ui/hakimo/data-access';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button, InputField } from '@hakimo-ui/shared/ui-base';
import clsx from 'clsx';
import {
  MouseEventHandler,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import MiniAudioPlayer from '../mini-audio-player/MiniAudioPlayer';
import { getFinalText } from '../util';

interface Props {
  text: string;
  updateAudioFile: (file: File) => void;
  prefetch?: boolean;
  inputAfterWordCount?: number;
  placeholder?: string;
  updateFinalTemplateText?: (text: string) => void;
  isInputValueInvalid?: boolean;
  showFetchAction?: boolean;
  alignment?: 'vertical' | 'horizontal';
  trackingMeta?: Record<string, unknown>;
}

export function TextToSpeech(props: Props) {
  const {
    text,
    prefetch,
    inputAfterWordCount,
    updateAudioFile,
    placeholder,
    updateFinalTemplateText,
    isInputValueInvalid,
    showFetchAction = true,
    alignment = 'horizontal',
    trackingMeta,
  } = props;
  const [inputValue, setInputValue] = useState('');
  const [audioSrc, setAudioSrc] = useState<string>('');
  const [fetchFailed, setFetchFailed] = useState(false);

  const { isLoading, fetchAudio } = useTextToSpeech(trackingMeta);
  const hasFetchedAudio = useRef(false);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value);
    updateFinalTemplateText &&
      updateFinalTemplateText(
        getFinalText(text, inputAfterWordCount, e.currentTarget.value)
      );
  };

  let inputNode;
  let firstPart = '';
  let lastPart = '';

  if (inputAfterWordCount !== undefined) {
    const splitTextArr = text.split(' ');
    const inputField = (
      <InputField
        type="text"
        required
        error={isInputValueInvalid}
        onChange={onInputChange}
        value={inputValue}
        placeholder={placeholder}
      />
    );
    if (splitTextArr.length > 1) {
      firstPart = splitTextArr.slice(0, inputAfterWordCount).join(' ');
      lastPart = splitTextArr.slice(inputAfterWordCount).join(' ');
      inputNode = (
        <>
          {firstPart} <span className="mx-2 max-w-[8rem]">{inputField}</span>{' '}
          {lastPart}
        </>
      );
    } else {
      inputNode = <span className="w-[40rem]">{inputField}</span>;
    }
  } else {
    inputNode = text;
  }

  const fetchAudioAndSetSrc = useCallback(async () => {
    try {
      const { audioUrl, file } = await fetchAudio(
        getFinalText(text, inputAfterWordCount, inputValue)
      );
      setFetchFailed(false);
      setAudioSrc(audioUrl);
      updateAudioFile(file);
    } catch {
      setFetchFailed(true);
      toast('Failed to fetch talkdown audio. Please retry.', {
        type: 'error',
      });
    }
  }, [fetchAudio, inputAfterWordCount, inputValue, text, updateAudioFile]);

  useEffect(() => {
    if (prefetch && !hasFetchedAudio.current) {
      hasFetchedAudio.current = true;
      fetchAudioAndSetSrc();
    }
  }, [fetchAudioAndSetSrc, prefetch]);

  const onClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    fetchAudioAndSetSrc();
  };

  return (
    <div
      className={clsx(
        alignment === 'horizontal' &&
          'grid w-[62rem] grid-cols-8 justify-items-center',
        alignment === 'vertical' && 'flex flex-col items-center space-y-3'
      )}
    >
      <div className="col-span-6 flex items-center justify-self-start">
        {inputNode}
      </div>
      <span className="flex items-center">
        <MiniAudioPlayer src={audioSrc} isLoading={isLoading} />
      </span>
      {(showFetchAction || fetchFailed) && (
        <Button
          onClick={onClick}
          loading={isLoading}
          disabled={inputAfterWordCount !== undefined && inputValue === ''}
        >
          <span className="text-xs">
            {fetchFailed ? 'Retry' : audioSrc ? 'Refetch' : 'Fetch'} Audio
          </span>
        </Button>
      )}
    </div>
  );
}

export default memo(TextToSpeech);
